/**
 * @file CustomersList.tsx
 * @description Admin-facing customers list + inline "+ new customer" form.
 *   Status badge + project count. Row click → /customers/:id.
 * @module Components
 * @security Invite goes through POST /admin/invitations (admin-gated); UI
 *   is just a UX guard.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CustomerRow, listCustomers } from '../../lib/admin-api';
import { EmptyState } from '../common/EmptyState';
import { SkeletonRows } from '../common/Skeleton';
import { AdminSummaryChips } from './AdminSummaryChips';
import { CustomerRowItem } from './CustomerRowItem';
import { NewCustomerForm } from './NewCustomerForm';
import {
  applyCustomerFilters,
  CustomersFilters,
  EMPTY_FILTERS,
  type CustomerFilters,
} from './CustomersFilters';

export function CustomersList(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { rows, loading, error, refresh } = useCustomersList();
  const [filters, setFilters] = useState<CustomerFilters>(EMPTY_FILTERS);
  const visible = applyCustomerFilters(rows, filters);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t('customers.title')}</h1>
      </div>
      <p className="text-sm text-slate-500">{t('customers.subtitle')}</p>
      <AdminSummaryChips />
      <NewCustomerForm locale={i18n.language === 'he' ? 'he' : 'en'} onCreated={refresh} />
      {error !== null ? <p className="text-sm text-red-600">{error}</p> : null}
      {rows.length > 0 ? (
        <CustomersFilters
          filters={filters}
          onChange={setFilters}
          totalCount={rows.length}
          filteredCount={visible.length}
          allRows={rows}
        />
      ) : null}
      {loading ? (
        <SkeletonRows count={4} rowClassName="h-14 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState icon="👥" title={t('customers.empty')} />
      ) : visible.length === 0 ? (
        <EmptyState icon="🔍" title={t('customers.filters.no_match')} />
      ) : (
        <ul className="space-y-2">
          {visible.map((c) => (
            <CustomerRowItem key={c.id} customer={c} />
          ))}
        </ul>
      )}
    </div>
  );
}

function useCustomersList(): {
  rows: CustomerRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listCustomers()
      .then((res) => {
        setRows(res.customers);
        setError(null);
      })
      .catch(() => setError(t('customers.errors.load')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  return { rows, loading, error, refresh: load };
}
